import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import {
  Events,
  LoadingController,
  ModalController,
  NavController,
  NavParams,
} from "ionic-angular";
import moment from "moment";
import { forkJoin } from "rxjs/observable/forkJoin";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { AuthService } from "../../app/auth/auth.service";
import Activity from "../../models/company-resources/activity";
import ActivityGridItem from "../../models/company-resources/activity-grid";
import SortOptions from "../../models/content-wrapper/sort-options";
import LookupValue from "../../models/patient-chart/lookupValue";
import { Patient } from "../../models/patient-chart/patient";
import CompanyResourceSummary from "../../models/resource-management/company-resource-summary";
import ProfessionalProvider from "../../models/resource-management/professional-provider";
import VolunteerMatchTerm from "../../models/resource-management/volunteers/volunteer-match-term";
import { ActivityLogEditPage } from "../../pages/company-resources/activity-log-edit/activity-log-edit";
import { NavBarServiceProvider } from "../../providers/nav-bar-service/nav-bar-service";
import { PatientChartService } from "../../providers/patient-charts/patient-chart.service";
import { ResourceManagementService } from "../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../providers/state-management/state-management.service";
import { VolunteerManagementServiceProvider } from "../../providers/volunteer-management-service/volunteer-management-service";
import SelectOption from "../common/multi-select-dropdown/selection-option";

@Component({
  selector: "activity-log-grid",
  templateUrl: "activity-log-grid.html",
})
export class ActivityLogGridComponent implements OnInit {
  private featureToggleProviderFlatUI: boolean;

  static LIMIT: number = 10;
  private spinnerCounter: number = 0;
  private totalProviders: number;
  private spinner: any = null;
  sortOptions: SortOptions = new SortOptions("last_name", "asc");
  public currentPage: number;
  public totalPageCount: number;
  private selectedProviders: Map<number, ProfessionalProvider> = new Map<
    number,
    ProfessionalProvider
  >();
  private _credentialTypes: LookupValue[];
  private _volunteerTypes: LookupValue[];
  private _volunteerActivityCategories: VolunteerMatchTerm[] = [];
  private _volunteerMatchTerms: VolunteerMatchTerm[] = [];
  private _disciplineItems: SelectOption[] = null;
  private _activityTypeItems: SelectOption[] = null;
  private selectedDisciplinesFilter: string[] = [];
  private selectedActivityType: number[] = [];
  private selectedStartDate: string = "";
  private selectedEndDate: string = "";
  private query: string = "";
  gridForm: FormGroup;
  private dateMin: any = "";
  private dateMax: any = "";
  datePlacementPreference: PlacementArray = ["top-left"];

  activityLog: ActivityGridItem[] = [];

  get credentialTypes(): LookupValue[] {
    return this._credentialTypes;
  }

  @Input()
  set credentialTypes(value: LookupValue[]) {
    this._credentialTypes = value;
  }

  get volunteerTypes(): LookupValue[] {
    return this._volunteerTypes;
  }

  @Input()
  set volunteerTypes(value: LookupValue[]) {
    this._volunteerTypes = value;
  }

  get volunteerActivityCategories(): any {
    return this._volunteerActivityCategories;
  }

  @Input()
  set volunteerActivityCategories(value: any) {
    this._volunteerActivityCategories = value;
  }

  @Input()
  set volunteerMatchTerms(value: any) {
    this._volunteerMatchTerms = value;
  }

  get volunteerMatchTerms(): any {
    return this._volunteerMatchTerms;
  }

  @Input()
  set disciplineItems(value: any) {
    this._disciplineItems = value;
  }

  get disciplineItems(): any {
    return this._disciplineItems;
  }

  @Input()
  set activityTypeItems(value: any) {
    this._activityTypeItems = value;
  }

  get activityTypeItems(): any {
    return this._activityTypeItems;
  }

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public modalController: ModalController,
    public resourceManagementService: ResourceManagementService,
    public volunteerManagementService: VolunteerManagementServiceProvider,
    private patientChartService: PatientChartService,
    private loader: LoadingController,
    private stm: StateManagementService,
    private fb: FormBuilder,
    private navBarSvc: NavBarServiceProvider,
    private events: Events,
    private authSvc: AuthService
  ) {
    this.featureToggleProviderFlatUI =
      this.navBarSvc.featureToggleOn("Provider Flat UI");
    if (this.featureToggleProviderFlatUI === undefined)
      this.featureToggleProviderFlatUI = true;
    this.gridForm = fb.group({
      query: "",
      activityLogStartDate: "",
      activityLogEndDate: "",
      discipline: "",
      activityType: "",
      hireToPresent: "",
    });

    this.gridForm.get("discipline").valueChanges.subscribe((x: string[]) => {
      this.selectedDisciplinesFilter = x;
      this.loadCompanyResource(0);
    });

    this.gridForm
      .get("query")
      .valueChanges.pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((x: string) => {
        this.query = x;
        this.loadCompanyResource(0);
      });

    this.gridForm.get("activityType").valueChanges.subscribe((x: number[]) => {
      this.selectedActivityType = x;
      this.loadCompanyResource(0);
    });

    this.gridForm
      .get("activityLogStartDate")
      .valueChanges.subscribe((x: string) => {
        if (!x || x === "") return;

        let n = this.filterDiff();
        if (n > 0) {
          this.gridForm.controls["activityLogEndDate"].setValue(
            moment.utc(x).add(7, "days").format("YYYY-MM-DD"),
            { emitEvent: false }
          );
          this.selectedEndDate = moment
            .utc(this.gridForm.controls["activityLogEndDate"].value)
            .format("YYYY-MM-DD");
        }

        this.selectedStartDate = moment.utc(x).format("YYYY-MM-DD");

        this.loadCompanyResource(0);
      });

    this.gridForm.get("activityLogEndDate").valueChanges.subscribe((x) => {
      if (!x || x === "") return;

      let n = this.filterDiff();
      if (n > 0) {
        this.gridForm.controls["activityLogStartDate"].setValue(
          moment.utc(x).subtract(7, "days").format("YYYY-MM-DD"),
          { emitEvent: false }
        );
        this.selectedStartDate = moment
          .utc(this.gridForm.controls["activityLogStartDate"].value)
          .format("YYYY-MM-DD");
      }

      this.selectedEndDate = moment.utc(x).format("YYYY-MM-DD");

      this.loadCompanyResource(0);
    });

    let now = new Date();
    const today = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    const eightyYearsAgo = {
      year: now.getFullYear() - 80,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };

    this.dateMin = eightyYearsAgo;
    this.dateMax = today;
  }

  filterDiff(): number {
    let moment1 = moment.utc(
      this.gridForm.controls["activityLogEndDate"].value
    );
    return moment
      .utc(this.gridForm.controls["activityLogStartDate"].value)
      .diff(moment1, "seconds");
  }

  ngOnInit(): void {
    this.loadCompanyResource(0);
  }

  addActivityLog() {
    let modal = this.modalController.create(
      ActivityLogEditPage,
      {
        grid: this,
        page: this.currentPage,
      },
      { enableBackdropDismiss: false }
    );
    modal
      .present()
      .then((res) => {})
      .catch((err) => {
        console.error(err);
      });
    modal.onDidDismiss(() => {});
  }

  openActivityLog(a: ActivityGridItem) {
    if (a) {
      this.increaseSpinnerCount("Editing activity ...");

      let observables = [];
      // TODO Fix BBP
      observables.push(this.resourceManagementService.getActivity(a.id));
      if (a.patientChartId) {
        observables.push(
          this.patientChartService.getPatientById(a.patientChartId, true)
        );
      }

      forkJoin(observables).subscribe(
        ([eeActivity, patientChart]: [Activity, Patient]) => {
          let modal = this.modalController.create(
            ActivityLogEditPage,
            {
              activityGridItem: a,
              activity: eeActivity,
              activityId: eeActivity.id,
              patient: patientChart,
              grid: this,
              page: this.currentPage,
            },
            { enableBackdropDismiss: false }
          );
          this.decreaseSpinnerCount();
          modal
            .present()
            .then((res) => {})
            .catch((err) => {
              console.error(err);
              this.decreaseSpinnerCount();
            });
          modal.onDidDismiss(() => {});
        },
        (err) => {
          console.error(err);
          this.decreaseSpinnerCount();
        }
      );
    } else {
      console.error(
        "Unable to open activity log item. No patientChartId found."
      );
    }
  }

  clearFilter() {
    this.selectedStartDate = "";
    this.selectedEndDate = "";
    this.selectedDisciplinesFilter = [];
    this.selectedActivityType = [];
    // gridForm.reset blanks the query INPUT but not the field the request is
    // built from, so the cleared text box kept filtering.
    this.query = "";

    this.gridForm.reset({}, { emitEvent: false });
    this.loadCompanyResource(0);
  }

  onPageChange(selectedPage: number) {
    this.loadCompanyResource(selectedPage);
  }

  loadCompanyResource(selectedPage: number): void {
    this.increaseSpinnerCount(`Loading activity log...`);

    this.resourceManagementService
      .getActivities(
        selectedPage,
        ActivityLogGridComponent.LIMIT,
        this.selectedDisciplinesFilter,
        this.selectedActivityType,
        this.selectedStartDate,
        this.selectedEndDate,
        this.sortOptions.column,
        this.sortOptions.direction
      )
      .pipe()
      .take(1)
      .subscribe(
        (x) => {
          // Guard the body: a throw in here skips decreaseSpinnerCount(), which
          // leaves the blocking overlay up and the grid stranded mid-load.
          const page: any = x || {};
          this.totalPageCount = page.totalPages;
          this.totalProviders = page.totalElements;
          this.currentPage = page.pageable
            ? page.pageable.pageNumber
            : selectedPage;
          this.activityLog = page.content || [];
          this.activityLog.forEach((a) => {
            a.patientChart$ = this.patientChartService.getPatientChartByQbosId(
              a.patientChartId
            );
          });
          this.decreaseSpinnerCount();
        },
        (error) => {
          console.error(error);
          this.decreaseSpinnerCount();
        }
      );
  }

  arrowClick(p: CompanyResourceSummary) {
    p.isSelected = !p.isSelected;
    if (p.isSelected && p.id) {
      if (!this.selectedProviders.has(p.id)) {
        p.$provider = this.resourceManagementService.getCredentialedResource(
          p.id
        );
      }
    }
  }

  sortBy(selectedCol) {
    this.currentPage = 0;
    if (this.sortOptions.column === selectedCol) {
      this.sortOptions.direction =
        this.sortOptions.direction === "asc" ? "desc" : "asc";
    } else {
      this.sortOptions.column = selectedCol;
      this.sortOptions.direction = "asc";
    }

    this.loadCompanyResource(0);
  }

  // incremental loader function
  increaseSpinnerCount(message: string = "Loading ...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && !this.spinner) {
      this.spinner = this.loader.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  // decremental loader function
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  ionViewCanEnter() {
    return this.authSvc.authenticated();
  }
}



// WEBPACK FOOTER //
// ./src/components/activity-log-grid/activity-log-grid.ts